"use server";

import { cookies } from "next/headers";
import { db } from "@/lib/db";
import { sendInviteRequestToAdmins } from "@/lib/emails";

export type InviteRequestResult = { error?: string; success?: string };

const INVITE_REQUEST_COOKIE = "rsvp_invite_requested";
const INVITE_REQUEST_SUCCESS =
  "Your request has been sent. If an invitation is available, you'll hear from us by email.";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export async function requestInvite(
  _prev: InviteRequestResult | null,
  formData: FormData
): Promise<InviteRequestResult> {
  const jar = await cookies();
  if (jar.get(INVITE_REQUEST_COOKIE)?.value) {
    return { success: INVITE_REQUEST_SUCCESS };
  }

  const name = String(formData.get("name") ?? "").trim();
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase();
  const institution = String(formData.get("institution") ?? "").trim();
  const event = String(formData.get("event") ?? "").trim();
  const note = String(formData.get("note") ?? "").trim();

  if (!name || !email || !institution || !event) {
    return {
      error: "Name, email, institution, and event are required.",
    };
  }
  if (!email.includes("@")) {
    return { error: "A valid email address is required." };
  }
  if (name.length > 200 || institution.length > 200 || event.length > 200) {
    return { error: "One or more fields are too long." };
  }
  if (note.length > 2000) {
    return { error: "Note is too long." };
  }

  const admins = await db.user.findMany({
    select: { email: true },
    orderBy: { createdAt: "asc" },
  });
  if (admins.length === 0) {
    return {
      error: "Unable to send your request right now. Please try again later.",
    };
  }

  await sendInviteRequestToAdmins(admins, {
    name,
    email,
    institution,
    event,
    note,
  });

  jar.set(INVITE_REQUEST_COOKIE, "1", {
    httpOnly: true,
    sameSite: "lax",
    secure: process.env.NODE_ENV === "production",
    maxAge: ONE_YEAR_SECONDS,
    path: "/",
  });

  return { success: INVITE_REQUEST_SUCCESS };
}
